<template>
  <!-- Один раз после входа в приложении: иначе push включают только те,
       кто сам дошёл до страницы уведомлений -->
  <Transition name="push-offer">
    <div v-if="visible" class="push-offer" role="dialog" :aria-label="t('pwa.pushOffer.title')">
      <div class="push-offer__content">
        <div class="push-offer__text">
          <span class="material-symbols-outlined push-offer__icon">notifications_active</span>
          <div>
            <p class="push-offer__title">{{ t('pwa.pushOffer.title') }}</p>
            <p class="push-offer__description">
              {{ t('pwa.pushOffer.description', { section: t('nav.notifications') }) }}
            </p>
          </div>
        </div>
        <p v-if="failed" class="push-offer__error">{{ t('notificationsPage.push.error') }}</p>
        <div class="push-offer__actions">
          <button class="push-offer__btn push-offer__btn--primary" :disabled="busy" @click="accept">
            {{ t('pwa.pushOffer.enable') }}
          </button>
          <button class="push-offer__btn" :disabled="busy" @click="visible = false">
            {{ t('pwa.later') }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { onBeforeUnmount, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { t } from '../services/i18nService'
import { hasNative } from '../services/nativeBridge'
import { getCurrentUser, isImpersonated } from '../services/authService'
import { PUSH_STATE, enablePush, fetchPushConfig, getPushState } from '../services/pushService'

const props = defineProps({
  // App.vue: вход выполнен, и это рабочая страница, а не вход, блокировка или чужая ссылка
  active: { type: Boolean, default: false },
})

const OFFER_DELAY = 3000
const TOUR_RETRY = 5000
// Демо один на всех: подписка гостя показала бы его телефон следующим гостям.
// Список зеркалит DEMO_RESTRICTED_LOGINS в backend/client/index.php
const DEMO_LOGINS = ['demo', 'demobile', 'demo_admin']

const route = useRoute()

const visible = ref(false)
const busy = ref(false)
const failed = ref(false)
let timer = null

// Ключ на пользователя: в одном приложении на телефоне могут входить разные люди
const seenKey = () => {
  const user = getCurrentUser()
  return user?.id ? `app-push-offer:${user.isSubUser ? 'sub' : 'user'}:${user.id}` : null
}
const wasSeen = (key) => {
  try { return !!localStorage.getItem(key) } catch { return true }
}
const markSeen = (key) => {
  try { localStorage.setItem(key, String(Date.now())) } catch {}
}

// Смотрим на страницу: состояние тура у каждого useTour() своё, а driver.js вешает класс
// на body. shouldShowOnboarding() тут не подходит: тур стартует только при запуске
// приложения, и после входа через форму он «не пройден» всю сессию
const isTourRunning = () => document.body.classList.contains('driver-active')

const check = async () => {
  const key = seenKey()
  if (!key || !props.active || wasSeen(key)) return
  if (isImpersonated() || DEMO_LOGINS.includes(getCurrentUser()?.username)) return
  // Окно поверх тура мешало бы: дождёмся, пока человек его закроет
  if (isTourRunning()) {
    timer = setTimeout(check, TOUR_RETRY)
    return
  }
  try {
    const { state } = await getPushState()
    // Уже включено или запрещено в настройках телефона: спрашивать незачем
    if (state === PUSH_STATE.ON || state === PUSH_STATE.DENIED) {
      markSeen(key)
      return
    }
    if (state !== PUSH_STATE.OFF) return
    const config = await fetchPushConfig()
    if (!config?.enabled || !props.active) return
  } catch {
    return
  }
  markSeen(key)
  visible.value = true
}

const schedule = () => {
  clearTimeout(timer)
  if (!hasNative()) return
  if (!props.active) {
    visible.value = false
    return
  }
  timer = setTimeout(check, OFFER_DELAY)
}

// Системный запрос разрешения показывается только по нажатию, поэтому и нужна эта кнопка
const accept = async () => {
  busy.value = true
  failed.value = false
  try {
    await enablePush()
    visible.value = false
  } catch {
    failed.value = true
  } finally {
    busy.value = false
  }
}

watch(() => [props.active, route.fullPath], schedule, { immediate: true })
onBeforeUnmount(() => clearTimeout(timer))
</script>

<style scoped>
.push-offer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10001;
  padding: 16px;
  padding-bottom: max(16px, env(safe-area-inset-bottom));
  background: var(--bg-card);
  border-top: 1px solid var(--border-default);
  box-shadow: var(--shadow-md);
}

.push-offer__content {
  max-width: 600px;
  margin: 0 auto;
}

.push-offer__text {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.push-offer__icon {
  flex-shrink: 0;
  font-size: 28px;
  color: var(--primary);
}

.push-offer__title {
  margin: 0 0 4px;
  color: var(--text-heading);
  font-size: var(--text-base);
  font-weight: 600;
}

.push-offer__description {
  margin: 0;
  color: var(--text-secondary);
  font-size: var(--text-sm);
}

.push-offer__error {
  margin: 10px 0 0;
  color: var(--danger-text);
  font-size: var(--text-sm);
}

.push-offer__actions {
  display: flex;
  gap: 8px;
  margin-top: 14px;
}

.push-offer__btn {
  flex: 1;
  min-height: 44px;
  padding: 10px 14px;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--bg-card);
  color: var(--text-primary);
  font-size: var(--text-sm);
  font-weight: 500;
  cursor: pointer;
}

.push-offer__btn--primary {
  flex: 2;
  border-color: var(--primary);
  background: var(--primary);
  color: var(--text-inverse);
  font-weight: 600;
}

.push-offer__btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.push-offer-enter-active,
.push-offer-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.push-offer-enter-from,
.push-offer-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
